import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import api from '@/admin/lib/api'
import { toastApiError } from '@/shared/lib/api-error'

/**
 * Downloads a report as CSV or XLSX from GET /api/reports/<key>/?download=...
 *
 * Mirrors `use-table-export`'s blob-download contract (Content-Disposition
 * filename, toast, loading state) rather than re-implementing it. `params`
 * carries the report's active filters + date window, so the export matches
 * what the preview shows.
 */
export type ReportExportFormat = 'csv' | 'xlsx'

const CONTENT_TYPES: Record<ReportExportFormat, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

export function useReportExport() {
  const [exportingKey, setExportingKey] = useState<string | null>(null)

  const exportReport = useCallback(
    async (
      key: string,
      format: ReportExportFormat,
      params: Record<string, string> = {},
      /** Another endpoint serving the same ``download`` contract (a dashboard card). */
      endpoint = `/reports/${key}/`,
    ) => {
      setExportingKey(`${key}:${format}`)
      try {
        const response = await api.get(endpoint, {
          params: { ...params, download: format },
          responseType: 'blob',
        })

        const blob = new Blob([response.data], { type: CONTENT_TYPES[format] })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        const disposition = response.headers['content-disposition']
        let filename = `${key}.${format}`
        if (disposition) {
          const match = disposition.match(/filename="?([^";\n]+)"?/)
          if (match?.[1]) filename = match[1]
        }

        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        toast.success('Export downloaded')
      } catch (err) {
        // A blob request carries the JSON error (e.g. the row cap) as a Blob.
        const data = (err as { response?: { data?: unknown } })?.response?.data
        if (data instanceof Blob) {
          try {
            const body: unknown = JSON.parse(await data.text())
            const message = (body as { message?: unknown } | null)?.message
            if (typeof message === 'string' && message) {
              toast.error(message)
              return
            }
          } catch {
            // not JSON - fall through to the generic toast
          }
        }
        toastApiError(err, 'Export failed')
      } finally {
        setExportingKey(null)
      }
    },
    [],
  )

  return { exportReport, exportingKey }
}
